import { useState } from 'react'
import { useAppDispatch } from '@/hooks'
import { useSurveyResponder } from '@/app/components/SurveyObserver'
import { formatResponsesForSurvey } from '@/app/features/onboarding/OnboardingQuestionnaire/useViewModel'
import { QuestionnairePageValue } from '@/app/components'
import { getCurrentUser, USER_SUCCESS } from '@/actions/user'
import { getPlans } from '@/actions/plans'
import { addHome } from '@/actions/homes'
import { getUtilities } from '@/actions/utilities'
import { User } from '@/types'
import { shouldNavigateUserToProgramOnboarding } from '@/app/components/Questionnaire/utils'
import { PayloadAction } from '@reduxjs/toolkit'
import { useNavigation } from '@/app/hooks'
import {
  filterAvailablePrograms,
  populateProgramViewConfigFromCMS,
  programsNewCollection,
  ProgramsNewModel,
} from '@/reducers/programsNew'
import useApiDispatch, {
  ApiResult,
} from '@/hooks/useApiDispatch/useApiDispatch'
import { ResourceModelQueryResult } from '@/request/types'
import { trackAddressSubmitted } from '@/logging/segment'

export interface UseViewModelReturnType {
  address: string | null
  isSubmitting: boolean
  handleAddressChange: (event: unknown) => void
  handleSubmit: () => Promise<void>
}

export function useGetPostAddressOnboardingRoute() {
  const dispatch = useAppDispatch()
  const { apiDispatch } = useApiDispatch()

  async function getPostAddressOnboardingRoute() {
    const [, , _userRes] = await Promise.all([
      dispatch(getUtilities()),
      dispatch(getPlans()),
      dispatch(getCurrentUser()),
    ])

    const userRes = _userRes as PayloadAction<User>

    if (userRes?.type === USER_SUCCESS) {
      const { shouldNavigate, destination } =
        shouldNavigateUserToProgramOnboarding(userRes.payload)

      if (shouldNavigate) {
        return destination
      }
    }

    const multiProgramResponse: ApiResult<
      ResourceModelQueryResult<ProgramsNewModel>[]
    > = await apiDispatch(programsNewCollection.actions.fetch())

    if (!multiProgramResponse.ok || multiProgramResponse.data?.length === 0) {
      return '/onboarding'
    }

    const mappedConfig = await Promise.all(
      multiProgramResponse.data.map(populateProgramViewConfigFromCMS),
    )

    const availableMultiPrograms = filterAvailablePrograms(mappedConfig, true)

    if (availableMultiPrograms.length > 0) {
      dispatch(programsNewCollection.actions.invalidate())
      return '/onboarding/program-finder'
    }
    return '/onboarding'
  }

  return { getPostAddressOnboardingRoute }
}

export function useViewModel(): UseViewModelReturnType {
  const navigation = useNavigation()
  const dispatch = useAppDispatch()
  const { getPostAddressOnboardingRoute } = useGetPostAddressOnboardingRoute()
  const [address, setAddress] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { submitResponse, activeSurveyResponse } =
    useSurveyResponder('Onboarding')

  const handleAddressChange = (event: unknown) => {
    setAddress(event as string)
  }

  const handleSubmit = async () => {
    if (!address) return

    setIsSubmitting(true)
    try {
      // Format the address for the survey system
      const formattedResponse: QuestionnairePageValue = {
        key: 'homeAddress',
        order: 0,
        value: [address],
      }

      // Use formatResponsesForSurvey to properly format the response
      const formattedResponses = formatResponsesForSurvey([formattedResponse])

      // Submit the address to the survey system
      await submitResponse(
        { ...activeSurveyResponse?.response, ...formattedResponses },
        false,
      )

      await dispatch(addHome({ google_place_id: address }))

      // Track address submission for analytics
      trackAddressSubmitted()

      const postAddressOnboardingRoute = await getPostAddressOnboardingRoute()

      // Redirect to the main onboarding flow
      navigation.push(postAddressOnboardingRoute)
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    address,
    isSubmitting,
    handleAddressChange,
    handleSubmit,
  }
}
